import { useMemo, useState, type CSSProperties } from 'react'
import { motion } from 'framer-motion'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import ShieldMoonRoundedIcon from '@mui/icons-material/ShieldMoonRounded'
import WallpaperRoundedIcon from '@mui/icons-material/WallpaperRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { ACTIVITY_TRACKS, ACTIVITY_TRACK_IDS, RARITY_BORDER_COLORS, resolveTrackLevel } from '../../utils/gamificationRegistry'
import {
  AUDIO_TRACKS,
  BACKGROUNDS,
  DEFAULT_AVATAR_ID,
  getAvatarById,
  THEMES,
} from '../../utils/cosmeticCatalog'
import { useGamification } from '../../context/useGamification'
import { CreditBalance } from '../Credits/CreditBalance'
import { usePreview } from '../../context/usePreview'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { useDisplayName } from '../../hooks/useDisplayName'
import { FilterChip, StatusChip, Textarea } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { Loader } from '../Common/Loader'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import { useNls } from '../../nls/nlsProvider'

const pct = (value: number, of: number) => (of <= 0 ? 100 : Math.min(100, Math.max(0, (value / of) * 100)))

const fadeInAt = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const, delay: index * 0.06 },
})

export const ProfileCanvas = () => {
  const { profile, isLoading, ledgerCredits, saveShowcase } = useGamification()
  const { previewAvatarId } = usePreview()
  const { user, userProfile } = useAuth()
  const { elements } = useTimeline()
  const displayName = useDisplayName(userProfile ?? user)
  const { t } = useNls()

  const [bioDraft, setBioDraft] = useState<string | null>(null)
  const [pinDraft, setPinDraft] = useState<string[] | null>(null)
  const [elDraft, setElDraft] = useState<string[] | null>(null)
  const [saving, setSaving] = useState(false)

  const earnedBadges = useMemo(() => {
    if (!profile) return []
    return ACTIVITY_TRACK_IDS.filter(
      (trackId) => resolveTrackLevel(profile.trackProgress[trackId]?.currentCount ?? 0, ACTIVITY_TRACKS[trackId].tiers) >= 1,
    )
  }, [profile])

  const goalElements = useMemo(
    () => elements.filter((element) => element.type === 'GOAL'),
    [elements],
  )

  if (isLoading || !profile) {
    return <div className="gTabBody deckV2"><div className="gPad appInlineLoadingState"><Loader size={140} label="Loading your profile..." /></div></div>
  }

  const avatar = getAvatarById(previewAvatarId ?? profile.equippedAvatarId) ?? getAvatarById(DEFAULT_AVATAR_ID)
  const pinnedBadges = pinDraft ?? profile.pinnedBadgeIds
  const pinnedEls = elDraft ?? profile.pinnedTimelineElementIds
  const equippedTheme = THEMES.find((item) => item.id === profile.equippedThemeId)
  const equippedBackground = BACKGROUNDS.find((item) => item.id === profile.equippedCanvasBgId)
  const equippedAudio = AUDIO_TRACKS.find((item) => item.id === profile.equippedAudioId)
  const completedGoalCount = goalElements.filter((element) => (element.progress ?? 0) >= 100).length
  const averageGoalProgress = goalElements.length > 0
    ? Math.round(goalElements.reduce((sum, element) => sum + (element.progress ?? 0), 0) / goalElements.length)
    : 0
  const earnedBadgeLabels = pinnedBadges
    .map((trackId) => ACTIVITY_TRACKS[trackId as keyof typeof ACTIVITY_TRACKS]?.title)
    .filter(Boolean)

  const avatarRarity = avatar?.rarity ?? 'COMMON'
  const rarityAccent: CSSProperties = { ['--char-accent' as string]: RARITY_BORDER_COLORS[avatarRarity] }
  const profileImageUrl = resolveProfileImageUrl(userProfile, user)
  const equippedTitle = profile.equippedTitle

  const toggle = (list: string[], id: string, max: number) =>
    list.includes(id) ? list.filter((value) => value !== id) : list.length >= max ? list : [...list, id]

  const persist = async (patch: Parameters<typeof saveShowcase>[0], reset: () => void) => {
    setSaving(true)
    try {
      await saveShowcase(patch)
      reset()
    } catch (error) {
      console.warn('[ProfileCanvas] save failed', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="gTabBody deckV2" style={rarityAccent}>
      <motion.section className="deckHero" {...fadeInAt(0)}>
        <div className="deckHeroInner">
          <div className="deckHeroPortrait" aria-label={`${avatar?.name ?? 'Avatar'} portrait`}>
            <span className="deckHeroPortraitInner">
              {profileImageUrl ? (
                <img src={profileImageUrl} alt="" />
              ) : (
                <span className="deckHeroPortraitEmoji">{avatar?.emoji ?? '🌱'}</span>
              )}
            </span>
          </div>
          <div className="deckHeroCore">
            <span className="deckHeroEyebrow">Presence Deck</span>
            <h2 className="deckHeroName">{displayName}</h2>
            {equippedTitle ? (
              <span className="deckHeroTitle" data-title={equippedTitle} aria-label={`Equipped title ${equippedTitle}`}>
                {equippedTitle}
              </span>
            ) : null}
            <div className="deckHeroChipRow">
              <span className="deckChip deckChip--rarity">{avatar?.name ?? 'Seedling'}</span>
              <StatusChip size="sm" tone="neutral">Lv {profile.level}</StatusChip>
              <StatusChip size="sm" tone="neutral">
                <CreditBalance value={ledgerCredits} variant="bare" scope="personal" label={null} /> credits
              </StatusChip>
              <StatusChip size="sm" tone="neutral">{earnedBadges.length} badge{earnedBadges.length === 1 ? '' : 's'}</StatusChip>
            </div>
            <div className="deckHeroXp" aria-label="Experience progress">
              <div className="deckHeroXpBar" role="progressbar" aria-valuemin={0} aria-valuemax={profile.xpNeededForNextLevel} aria-valuenow={profile.currentXp}>
                <div className="deckHeroXpFill" style={{ width: `${pct(profile.currentXp, profile.xpNeededForNextLevel)}%` }} />
              </div>
              <span className="deckHeroXpMeta">{profile.currentXp} / {profile.xpNeededForNextLevel} xp</span>
            </div>
          </div>
          <div className="deckHeroAside" aria-label="Profile signals">
            <div className="deckMetricGrid deckHeroAsideMetrics">
              <div className="deckMetric">
                <span className="deckMetricLabel">Goals</span>
                <span className="deckMetricValue">{goalElements.length}</span>
              </div>
              <div className="deckMetric">
                <span className="deckMetricLabel">Complete</span>
                <span className="deckMetricValue">{completedGoalCount}</span>
              </div>
              <div className="deckMetric">
                <span className="deckMetricLabel">Average</span>
                <span className="deckMetricValue">{averageGoalProgress}<small>%</small></span>
              </div>
              <div className="deckMetric">
                <span className="deckMetricLabel">Pinned</span>
                <span className="deckMetricValue">{pinnedEls.length}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section className="deckSection" {...fadeInAt(1)}>
        <div className="deckSectionHead">
          <div className="deckSectionHeadCopy">
            <h3 className="deckSectionTitle">Current Loadout</h3>
            <span className="deckSectionSub">The identity build you have equipped right now.</span>
          </div>
        </div>
        <div className="deckLoadout">
          <div className="deckLoadoutSlot">
            <span className="deckLoadoutIcon"><ShieldMoonRoundedIcon fontSize="inherit" /></span>
            <div className="deckLoadoutCopy">
              <span className="deckLoadoutLabel">Guardian</span>
              <span className="deckLoadoutValue">{avatar?.name ?? 'Seedling'}</span>
            </div>
          </div>
          <div className="deckLoadoutSlot">
            <span className="deckLoadoutIcon"><PaletteRoundedIcon fontSize="inherit" /></span>
            <div className="deckLoadoutCopy">
              <span className="deckLoadoutLabel">Theme</span>
              <span className="deckLoadoutValue">{equippedTheme?.name ?? 'Default'}</span>
            </div>
          </div>
          <div className="deckLoadoutSlot">
            <span className="deckLoadoutIcon"><WallpaperRoundedIcon fontSize="inherit" /></span>
            <div className="deckLoadoutCopy">
              <span className="deckLoadoutLabel">Canvas</span>
              <span className="deckLoadoutValue">{equippedBackground?.name ?? 'Default'}</span>
            </div>
          </div>
          <div className="deckLoadoutSlot">
            <span className="deckLoadoutIcon"><HeadphonesRoundedIcon fontSize="inherit" /></span>
            <div className="deckLoadoutCopy">
              <span className="deckLoadoutLabel">Soundscape</span>
              <span className="deckLoadoutValue">{equippedAudio?.name ?? 'Silence'}</span>
            </div>
          </div>
        </div>
      </motion.section>

      <div className="deckShowcaseGrid">
        <motion.section className="deckPanel" {...fadeInAt(2)}>
          <div className="deckPanelHead">
            <div>
              <h3 className="deckPanelTitle">Bio</h3>
              <span className="deckPanelSub">The intro shown on your profile page.</span>
            </div>
            {bioDraft === null ? (
              <IconButton variant="tertiary" size="sm" icon={<EditRoundedIcon fontSize="small" />} label="Edit" onClick={() => setBioDraft(profile.bioMarkdown)} />
            ) : (
              <div className="deckPanelActions">
                <IconButton variant="tertiary" size="sm" icon={<CloseRoundedIcon fontSize="small" />} label="Cancel" disabled={saving} onClick={() => setBioDraft(null)} />
                <IconButton
                  variant="primary"
                  size="sm"
                  icon={<SaveRoundedIcon fontSize="small" />}
                  label="Save"
                  disabled={saving}
                  onClick={() => void persist({ bioMarkdown: bioDraft }, () => setBioDraft(null))}
                />
              </div>
            )}
          </div>
          {bioDraft === null ? (
            profile.bioMarkdown ? (
              <p className="deckBioText">{profile.bioMarkdown}</p>
            ) : (
              <div className="deckEmpty">
                <strong>No bio yet</strong>
                <span>Tell people what you are building, what you care about, or what kind of teammate you are.</span>
              </div>
            )
          ) : (
            <Textarea
              className="deckBioEditor"
              value={bioDraft}
              maxLength={2000}
              rows={5}
              placeholder="Markdown supported. Share your mission..."
              onChange={setBioDraft}
              aria-label={t('NLSExperienceProfileBioAria')}
            />
          )}
        </motion.section>

        <motion.section className="deckPanel" {...fadeInAt(3)}>
          <div className="deckPanelHead">
            <div>
              <h3 className="deckPanelTitle">Pinned Badges</h3>
              <span className="deckPanelSub">Pin up to 4 earned mastery badges.</span>
            </div>
            {pinDraft === null ? (
              <IconButton variant="tertiary" size="sm" icon={<EditRoundedIcon fontSize="small" />} label="Edit" disabled={earnedBadges.length === 0} onClick={() => setPinDraft(profile.pinnedBadgeIds)} />
            ) : (
              <div className="deckPanelActions">
                <IconButton variant="tertiary" size="sm" icon={<CloseRoundedIcon fontSize="small" />} label="Cancel" disabled={saving} onClick={() => setPinDraft(null)} />
                <IconButton variant="primary" size="sm" icon={<SaveRoundedIcon fontSize="small" />} label="Save" disabled={saving} onClick={() => void persist({ pinnedBadgeIds: pinDraft }, () => setPinDraft(null))} />
              </div>
            )}
          </div>
          {pinDraft === null && earnedBadgeLabels.length > 0 && (
            <p className="deckPanelSub deckPanelSummary">{earnedBadgeLabels.join(' · ')}</p>
          )}
          {(pinDraft === null ? pinnedBadges : earnedBadges).length === 0 ? (
            <div className="deckEmpty">
              <strong>{pinDraft === null ? 'No badges pinned' : 'No badges earned yet'}</strong>
              <span>
                {pinDraft === null
                  ? 'Pin achievements you want people to see when they visit your profile.'
                  : 'Reach a track tier on the Achievements page to unlock badges.'}
              </span>
            </div>
          ) : (
            <div className="deckBadgeRow">
              {(pinDraft === null ? pinnedBadges : earnedBadges).map((trackId) => {
                const def = ACTIVITY_TRACKS[trackId as keyof typeof ACTIVITY_TRACKS]
                if (!def) return null
                const selected = pinnedBadges.includes(trackId)
                return (
                  <FilterChip
                    key={trackId}
                    selected={pinDraft !== null && selected}
                    disabled={pinDraft === null}
                    onClick={() => pinDraft !== null && setPinDraft(toggle(pinDraft, trackId, 4))}
                  >
                    <EmojiEventsRoundedIcon fontSize="inherit" /> {def.title}
                  </FilterChip>
                )
              })}
            </div>
          )}
        </motion.section>

        <motion.section className="deckPanel deckPanel--wide" {...fadeInAt(4)}>
          <div className="deckPanelHead">
            <div>
              <h3 className="deckPanelTitle">Pinned Timelines</h3>
              <span className="deckPanelSub">Goals that appear on your profile page.</span>
            </div>
            {elDraft === null ? (
              <IconButton variant="tertiary" size="sm" icon={<EditRoundedIcon fontSize="small" />} label="Edit" disabled={goalElements.length === 0} onClick={() => setElDraft(profile.pinnedTimelineElementIds)} />
            ) : (
              <div className="deckPanelActions">
                <IconButton variant="tertiary" size="sm" icon={<CloseRoundedIcon fontSize="small" />} label="Cancel" disabled={saving} onClick={() => setElDraft(null)} />
                <IconButton variant="primary" size="sm" icon={<SaveRoundedIcon fontSize="small" />} label="Save" disabled={saving} onClick={() => void persist({ pinnedTimelineElementIds: elDraft }, () => setElDraft(null))} />
              </div>
            )}
          </div>
          {(elDraft === null ? pinnedEls : goalElements.map((element) => element.id)).length === 0 ? (
            <div className="deckEmpty">
              <strong>{elDraft === null ? 'No timelines pinned' : 'No goals yet'}</strong>
              <span>
                {elDraft === null
                  ? 'Pin the goals that best represent what you are working on.'
                  : 'Create a goal from the Timeline to pin its progress here.'}
              </span>
            </div>
          ) : (
            <div className="deckPinnedTimelines">
              {(elDraft === null ? pinnedEls : goalElements.map((element) => element.id)).map((elementId) => {
                const element = goalElements.find((candidate) => candidate.id === elementId)
                if (!element) return null
                const selected = pinnedEls.includes(elementId)
                return (
                  <Pressable
                    key={elementId}
                    aria-pressed={elDraft !== null && selected}
                    className={`deckTimelinePin ${elDraft !== null && selected ? 'is-selected' : ''}`}
                    disabled={elDraft === null}
                    onClick={() => elDraft !== null && setElDraft(toggle(elDraft, elementId, 12))}
                  >
                    <span className="deckTimelinePinTitle">{element.title}</span>
                    <span className="deckTimelinePinBar">
                      <span className="deckTimelinePinFill" style={{ width: `${Math.round(element.progress ?? 0)}%` }} />
                    </span>
                  </Pressable>
                )
              })}
            </div>
          )}
        </motion.section>
      </div>
    </div>
  )
}
